import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderNumber, setOrderNumber] = useState("");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNumber(data.order_number || "");
        if (data.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          setState("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (attempts >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      {state === "checking" && (<>
        <Loader2 className="mx-auto h-12 w-12 animate-spin text-kys-accent" />
        <h1 className="mt-6 font-serif text-3xl text-kys-ink">Confirming your payment…</h1>
        <p className="mt-2 text-sm text-kys-muted">One moment while we confirm with Stripe.</p>
      </>)}
      {state === "paid" && (<>
        <CheckCircle2 className="mx-auto h-14 w-14 text-kys-accent" />
        <h1 className="mt-6 font-serif text-4xl text-kys-ink">Payment successful</h1>
        <p className="mt-3 text-sm text-kys-charcoal">Thank you! Your order is confirmed and paid.</p>
        {orderNumber && <p data-testid="order-number" className="mt-4 inline-block rounded-md border border-kys-blush-border bg-white px-5 py-2 font-mono text-lg text-kys-ink">{orderNumber}</p>}
        <p className="mx-auto mt-6 max-w-md text-xs text-kys-muted">Shipping and tracking will be added by the boutique. You'll receive an email once those are configured.</p>
        <Link to={orderNumber.startsWith("KYS-CUS") ? "/account" : "/shop"} data-testid="payment-success-cta" className="mt-8 inline-flex rounded-full bg-kys-ink px-7 py-3 text-sm font-semibold text-white hover:bg-kys-accent">{orderNumber.startsWith("KYS-CUS") ? "View my custom orders" : "Keep shopping"}</Link>
      </>)}
      {state === "pending" && (<>
        <Loader2 className="mx-auto h-12 w-12 text-kys-gold" />
        <h1 className="mt-6 font-serif text-3xl text-kys-ink">Payment processing</h1>
        <p className="mt-3 text-sm text-kys-charcoal">Your payment is still processing. If you completed checkout, your order {orderNumber} will confirm shortly.</p>
        <Link to="/" className="mt-8 inline-flex rounded-full bg-kys-ink px-7 py-3 text-sm font-semibold text-white hover:bg-kys-accent">Back home</Link>
      </>)}
      {state === "error" && (<>
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h1 className="mt-6 font-serif text-3xl text-kys-ink">Something went wrong</h1>
        <p className="mt-3 text-sm text-kys-charcoal">We couldn't verify this payment session.</p>
        <Link to="/cart" className="mt-8 inline-flex rounded-full bg-kys-ink px-7 py-3 text-sm font-semibold text-white hover:bg-kys-accent">Return to bag</Link>
      </>)}
    </div>
  );
}
